import { logger } from "@/helpers/tslog";

type SharePriceDataType = "Adjusted_Closing_Share_Price" | "Closing_Share_Price" | (string & {});
type CustomBuiltIndexConstituent = { name?: string; ticker?: string } | string;
type CustomBuiltIndex = { name?: string; indexName?: string; constituents?: ReadonlyArray<CustomBuiltIndexConstituent> };
type ModelDataShape = { endDate?: string; currency?: string; companies?: ReadonlyArray<{ dataType?: SharePriceDataType }> ; customBuiltIndex?: ReadonlyArray<CustomBuiltIndex> ; percentageChangeChart?: unknown; percentageChangeChartForm?: Record<string, unknown> };

// LLM Global State Data Types
type LLMGlobalStateData = {
  endDate?: string;
  timePeriod?: {
    startDate?: string;
    endDate?: string;
  };
  currency?: string;
  dataType?: SharePriceDataType;
  companies?: ReadonlyArray<{ dataType?: SharePriceDataType }>;
  customBuiltIndex?: ReadonlyArray<CustomBuiltIndex>;
  customBuiltIndicesForm?: ReadonlyArray<CustomBuiltIndex>;
  percentageChangeChart?: unknown;
  percentageChangeChartForm?: Record<string, unknown>;
  chartTitle?: string;
  chartSubtitleLeft?: string;
  chartSubtitleRight?: string;
  primaryCompany?: {
    ticker?: string;
    name?: string;
    exchange?: string;
  };
  peerCompanies?: ReadonlyArray<{
    ticker?: string;
    name?: string;
    exchange?: string;
  }>;
  selectedFeatures?: ReadonlyArray<string>;
  [key: string]: any; // Allow for additional properties
};

function toSharePriceLabel(dataType: SharePriceDataType): string {
  if (dataType === "Adjusted_Closing_Share_Price") return "Adjusted closing share price";
  if (dataType === "Closing_Share_Price") return "Closing share price";
  const cleaned = String(dataType).replace(/_/g, " ").toLowerCase();
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

function extractIndexName(index: CustomBuiltIndex): string | undefined {
  return index.name || index.indexName;
}

function extractConstituentName(c: CustomBuiltIndexConstituent): string {
  if (typeof c === "string") return c;
  return c.name || c.ticker || "Unknown";
}

export function generateSourceAndNotes(args?: {
  llmGlobalStateData?: LLMGlobalStateData;
}): { source: string; notes: string[] } {
  // Use LLM global state data if provided, otherwise fall back to modelData
  const data = args?.llmGlobalStateData;

  // Extract endDate from timePeriod or direct endDate
  const endDate = data?.timePeriod?.endDate || data?.endDate || "";
  const currency = data?.currency || "";
  
  // Extract dataType from direct property or companies array
  const dataType: SharePriceDataType = data?.dataType || 
    (data?.companies?.[0]?.dataType as SharePriceDataType) || 
    "Adjusted_Closing_Share_Price";

  const source = `AlphaScript data as of ${endDate}`;

  const sharePriceLabel = toSharePriceLabel(dataType);
  const disclosure1 = `${sharePriceLabel} data in ${currency} is used for all companies on the chart.`;

  const percentageChangeSelected = Boolean(
    data?.percentageChangeChart ||
    (data?.percentageChangeChartForm && Object.keys(data.percentageChangeChartForm || {}).length > 0) ||
    (data?.selectedFeatures && data.selectedFeatures.includes('percentage-change-chart'))
  );

  // Extract custom indexes from both possible locations
  const customIndexes: CustomBuiltIndex[] = [
    ...(Array.isArray(data?.customBuiltIndex) ? (data.customBuiltIndex as unknown as CustomBuiltIndex[]) : []),
    ...(Array.isArray(data?.customBuiltIndicesForm) ? (data.customBuiltIndicesForm as unknown as CustomBuiltIndex[]) : [])
  ];

  const indexNotes: string[] = customIndexes
    .map((idx) => {
      const name = idx.name || idx.indexName;
      if (!name) return "";
      const list = (idx.constituents || [])
        .map((c) => typeof c === "string" ? c : (c.name || c.ticker || "Unknown"))
        .filter(Boolean)
        .join(", ");
      return `${name} includes the following companies (on an adjusted closing share price basis): ${list}.`;
    })
    .filter(Boolean);



  const notes = [
    // Include disclosure only when percentage change chart is selected
    ...(percentageChangeSelected ? [disclosure1] : []),
    ...indexNotes,
  ];

  // Format notes as numbered list
  const formattedNotes = notes.map((note, index) => `${index + 1}.     ${note}`);

  logger.info("Source and notes generated:", { source, notes: formattedNotes });

  return { source, notes: formattedNotes };
}

// Utility: convert a numbered notes string into an array of strings without numeric prefixes
// Example input:
//  "1. First note\n2. Second note" -> ["First note", "Second note"]
export function parseNumberedListToArray(text: string): string[] {
  if (!text) return [];
  const byLines = String(text).split(/\r?\n/).filter((l) => l && l.trim().length > 0);
  const items = (byLines.length > 0 ? byLines : [String(text)])
    .flatMap((segment) => String(segment).split(/(?<=\.)\s+(?=\d+\.)/))
    .map((t) => t.trim().replace(/^\d+\.\s*/, "").trim())
    .filter((t) => t.length > 0);
  return items;
}

// New function specifically for LLM global state data
export function generateSourceAndNotesFromLLMData(llmGlobalStateData: LLMGlobalStateData): { source: string; notes: string[] } {
  return generateSourceAndNotes({ llmGlobalStateData });
}

export default generateSourceAndNotes;


